import { boolean, date, InferType, mixed, number, object, ObjectSchema, string } from "yup";
import { CriarFuncionarioContract } from "../contracts/criar-funcionario.contract";
import { AccountBankTypeEnum, PixKeyTypeEnum } from "../../enum";
import { ModalityEnum, SectorEnum, TypeBondEnum } from "../../../shared/utils/enum/enum";
import { UnitOfMeasureEnum } from "../../../shared/components/formulario/select/SelectUnitOfMeasure";

export const criarFuncionarioSchema: ObjectSchema<CriarFuncionarioContract> = object({
    typeBond: mixed<TypeBondEnum>()
        .oneOf(Object.values(TypeBondEnum), "Selecione um regime de trabalho valído")
        .required("É obrigatório informar o regime de trabalho."),

    sector: string().required("É obrigatório informar o setor do colaborador."),

    corporateName: string()
        .nullable()
        .default(null)
        .when("cnpj", {
            is: (cnpj: string) => !!cnpj && cnpj.trim() !== "",
            then: (schema) => schema.required("Razão Social é obrigatória"),
            otherwise: (schema) => schema.notRequired(),
        }),

    fullName: string()
        .nullable()
        .default(null)
        .when("cpf", {
            is: (cpf: string) => !!cpf && cpf.trim() !== "",
            then: (schema) => schema.required("O nome do funcionário é obrigatório"),
            otherwise: (schema) => schema.notRequired(),
        }),

    fantasyName: string().nullable().default(null),

    email: string().email("E-mail inválido").required("E-mail é obrigatório"),

    cnpj: string().default(""),
    cpf: string().default(""),

    dateOfBirth: date().required("Data de nascimento é obrigatória"),

    admissionDate: date()
        .default(() => new Date()), // valor atual como default

    phone: string().required("Contato é obrigatório"),

    active: boolean().required(),

    address: object().shape({
        zipCode: string().required("CEP é obrigatório"),
        street: string().required("Rua é obrigatória"),
        number: string().required("Número é obrigatório"),
        neighborhood: string().required("Bairro é obrigatório"),
        city: string().required("Cidade é obrigatória"),
        state: string().required("Estado é obrigatório"),
        complement: string().default(""),
    }),

    isResponsible: boolean().default(false),

    idWork: string()
        .nullable()
        .default(null),
    positionId:
        string()
            .nullable()
            .default(null),

    modality: mixed<UnitOfMeasureEnum>()
        .oneOf(Object.values(UnitOfMeasureEnum), "Selecione uma modalidade válida")
        .required("A modalidade é obrigatória"),

    accountBank: object().shape({
        bank: string().required("Banco é obrigatório"),
        agency: string().required("Agência é obrigatória"),
        account: string().required("Conta é obrigatória"),
        accountType: mixed<AccountBankTypeEnum>()
            .oneOf(Object.values(AccountBankTypeEnum), "Selecione um tipo de conta")
            .required("Tipo de conta é obrigatório"),
        pixKeyType: mixed<PixKeyTypeEnum>()
            .oneOf(Object.values(PixKeyTypeEnum), "Selecione uma chave Pix")
            .nullable()
            .default(null),
        pixKey: string()
            .nullable()
            .default(null)
            .when("pixKeyType", {
                is: (pixKeyType: string) => !!pixKeyType && pixKeyType.trim() !== "",
                then: (schema) => schema.required("A chave Pix é obrigatória"),
                otherwise: (schema) => schema.notRequired(),
            }),
    }),

    salary: number()
        .nullable()
        .default(null)
        .transform((value, originalValue) => {
            if (typeof originalValue === "string") {
                const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
                if (!cleaned) return undefined;
                const valorNumber = Number(cleaned);
                return isNaN(valorNumber) ? undefined : valorNumber;
            }
            return value;
        })
        .when("idWork", {
            is: (idWork: string | null) => !idWork, // Se idWork for nulo ou vazio
            then: (schema) =>
                schema.required(
                    "O salário é obrigatório quando o colaborador não está alocado em uma obra."
                ),
            otherwise: (schema) => schema.notRequired(),
        }),

    description: string()
        .nullable()
        .default(null)
});

export type CriarFuncionarioFormData = InferType<typeof criarFuncionarioSchema>;
